import { EntityRepository } from '@/interfaces'
import model from './model'
import type ObjectInterface from './interface'
import { hydrateEntityObject } from '@/entities/lib'
import graphQLClient from '@/lib/api/graphQLClient'
import { TFunction } from 'next-i18next'
import { useMemo } from 'react'
import useSWR from 'swr'

import {
  ALL_FILTER_LEAD_BLOCKS_QUERY,
  SINGLE_FILTER_LEAD_BLOCK_QUERY,
  CREATE_FILTER_LEAD_BLOCK_MUTATION,
  UPDATE_FILTER_LEAD_BLOCK_MUTATION,
  DELETE_FILTER_LEAD_BLOCK_MUTATION,
  FORCE_DELETE_FILTER_LEAD_BLOCK_MUTATION,
} from './graphql'

const hydrateObjects = (objects: Array<any>) => {
  return objects.map((object) => hydrateEntityObject(model, object))
}

const hydrateObject = (object: any) => {
  return hydrateEntityObject(model, object)
}
const cmsFilterLeadBlockRepository: EntityRepository = {
  getObject: async (id: string | number, includes = {}): Promise<any> => {
    const variables = { id: id, ...includes }
    return graphQLClient.request(SINGLE_FILTER_LEAD_BLOCK_QUERY, variables).then((data: any) => {
      return hydrateObject(data.filterLeadBlock)
    })
  },

  getObjects: async (includes = {}): Promise<any> => {
    return graphQLClient.request(ALL_FILTER_LEAD_BLOCKS_QUERY, includes).then((data: any) => {
      return hydrateObjects(data.filterLeadBlocks)
    })
  },

  useObject: (id: string | number, includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includes }), [id])
    const { data, error, mutate } = useSWR(paused ? null : [SINGLE_FILTER_LEAD_BLOCK_QUERY, variables], { revalidateOnFocus: false })
    return {
      object: !data ? data : hydrateObject(data.filterLeadBlock),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  useObjects: (includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => includes, [])
    const { data, error, mutate } = useSWR(paused ? null : [ALL_FILTER_LEAD_BLOCKS_QUERY, variables])
    return {
      objects: !data ? data : hydrateObjects(data.filterLeadBlocks),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  createObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(CREATE_FILTER_LEAD_BLOCK_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.filterLeadBlock)
    })
  },

  updateObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(UPDATE_FILTER_LEAD_BLOCK_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.filterLeadBlock)
    })
  },

  deleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(DELETE_FILTER_LEAD_BLOCK_MUTATION, variables).then((data: any) => {
      return data.deleteCmsFilterLeadBlock
    })
  },

  forceDeleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(FORCE_DELETE_FILTER_LEAD_BLOCK_MUTATION, variables).then((data: any) => {
      return data.forceDeleteCmsFilterLeadBlock
    })
  },
}

export default cmsFilterLeadBlockRepository
